import { Node, Edge } from '../models/scene';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';

type ClipboardState = {
  nodes: Node[];
  edges: Edge[];
};

let state: ClipboardState = { nodes: [], edges: [] };

function encodeUtf8Base64(value: string): string {
  if (typeof TextEncoder !== 'undefined' && typeof btoa === 'function') {
    const bytes = new TextEncoder().encode(value);
    let binary = '';
    const chunkSize = 0x8000;

    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
    }

    return btoa(binary);
  }

  return Buffer.from(value, 'utf-8').toString('base64');
}

function decodeUtf8Base64(value: string): string {
  if (typeof TextDecoder !== 'undefined' && typeof atob === 'function') {
    const binary = atob(value);
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }

  return Buffer.from(value, 'base64').toString('utf-8');
}

export const Clipboard = {
  set(nodes: Node[], edges: Edge[] = []): void {
    state = {
      nodes: nodes.map(n => ({ ...n })),
      edges: edges.map(e => ({ ...e }))
    };
    // Track copy usage (aggregated + first-time)
    if (nodes.length > 0 || edges.length > 0) {
      SessionMetrics.increment('copy_paste');
      SessionMetrics.firstTime('copy_paste');
    }
  },
  get(): { nodes: Node[]; edges: Edge[] } {
    return {
      nodes: state.nodes.map(n => ({ ...n })),
      edges: state.edges.map(e => ({ ...e }))
    };
  },
  clear(): void {
    state = { nodes: [], edges: [] };
  },
  has(): boolean {
    return state.nodes.length > 0 || state.edges.length > 0;
  },

  // ---- System clipboard integration ----
  encode(nodes: Node[], edges: Edge[] = []): string {
    const payload = {
      version: 1,
      type: 'creately-clipboard',
      timestamp: Date.now(),
      data: { nodes, edges }
    };
    const json = JSON.stringify(payload);
    // Encode JSON as UTF-8 before Base64 so diagram text from any language is supported.
    const base64 = encodeUtf8Base64(json);
    return `CREATELY_CLIPBOARD_V1:${base64}`;
  },

  decode(text: string): { nodes: Node[]; edges: Edge[] } | null {
    try {
      if (!text || !text.startsWith('CREATELY_CLIPBOARD_V1:')) return null;
      const base64 = text.substring('CREATELY_CLIPBOARD_V1:'.length);
      const json = decodeUtf8Base64(base64);
      const parsed = JSON.parse(json);
      if (parsed?.type !== 'creately-clipboard' || !parsed?.data) return null;
      const nodes = Array.isArray(parsed.data.nodes) ? parsed.data.nodes : [];
      const edges = Array.isArray(parsed.data.edges) ? parsed.data.edges : [];
      return { nodes, edges } as { nodes: Node[]; edges: Edge[] };
    } catch {
      return null;
    }
  },

  async writeToSystem(nodes: Node[], edges: Edge[] = []): Promise<void> {
    try {
      const text = this.encode(nodes, edges);
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback: hidden textarea
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch {
      // Ignore clipboard errors silently
    }
  },

  async readFromSystem(): Promise<{ nodes: Node[]; edges: Edge[] } | null> {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        return this.decode(text);
      }
    } catch {
      // Ignore read errors
    }
    return null;
  }
};
